<template>
  <div>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card elevation="3" rounded="xl">
            <!-- Page Header -->
            <v-card-title class="pb-3 pt-5 px-6">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <h2 class="text-h5 font-weight-bold primary--text mb-1">
                    Company Management
                  </h2>
                  <p class="text-body-2 grey--text ma-0">
                    Manage and monitor all business companies
                  </p>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="2"
                      rounded
                      to="/companies/create"
                      class="px-5"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Add New Company
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider class="mx-6"></v-divider>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-2 pt-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-3" color="primary">mdi-filter-variant</v-icon>
                    <span class="text-subtitle-1 font-weight-medium">Filter Companies</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      small
                      rounded="pill"
                      class="mr-3"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active Filter(s)
                    </v-chip>
                    <v-btn
                      text
                      small
                      color="error"
                      rounded
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left small>mdi-filter-remove</v-icon>
                      Clear All
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-6 pb-6">
              <v-row class="align-center">
                <!-- Search Field -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.search_keyword"
                    label="Search Companies"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    rounded
                    dense
                    clearable
                    placeholder="Name, email, admin, phone..."
                    @click:clear="(form.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" sm="6" md="3">
                  <v-select
                    v-model="form.is_active"
                    :items="statuses"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    rounded
                    dense
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>

                <!-- Additional filters can be added here -->
                <v-col cols="12" sm="6" md="3">
                  <!-- Placeholder for future filters -->
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Companies Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" rounded="lg">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="24">mdi-office-building</v-icon>
                    <span class="text-h6 font-weight-medium">Companies List</span>
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined class="mr-3">
                      <v-icon left small>mdi-office-building-outline</v-icon>
                      {{ count }} {{ count === 1 ? "Company" : "Companies" }} Found
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      Showing {{ (page - 1) * itemsPerPage + 1 }} -
                      {{ Math.min(page * itemsPerPage, count) }} of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-3">
                      <span class="text-caption grey--text mr-2">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn small outlined :value="10">10</v-btn>
                        <v-btn small outlined :value="25">25</v-btn>
                        <v-btn small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Button -->
                    <v-btn
                      small
                      color="success"
                      outlined
                      rounded
                      @click="exportToExcel"
                      class="px-3"
                    >
                      <v-icon left small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <table-skeleton v-if="isLoading" :rows="8" :cols="5" />

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="companies"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{ item.is_active ? "mdi-check-circle" : "mdi-close-circle" }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- Company Info Column -->
                <template #[`item.company_info`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="40" class="mr-3">
                      <v-img
                        v-if="item.logo_path"
                        :src="`${mediaUrl}${item.logo_path}`"
                        :alt="item.name"
                      ></v-img>
                      <v-icon v-else color="grey lighten-1">mdi-office-building</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">
                        {{ item.name || "N/A" }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ item.admin_name || "No admin assigned" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Contact Column -->
                <template #[`item.contact`]="{ item }">
                  <div>
                    <div class="d-flex align-center mb-1" v-if="item.email">
                      <v-icon small class="mr-1 grey--text">mdi-email</v-icon>
                      <span class="text-caption">{{ item.email }}</span>
                    </div>
                    <div class="d-flex align-center" v-if="item.phone">
                      <v-icon small class="mr-1 grey--text">mdi-phone</v-icon>
                      <span class="text-caption">{{ item.phone }}</span>
                    </div>
                    <div
                      v-if="!item.email && !item.phone"
                      class="text-caption grey--text"
                    >
                      No contact info
                    </div>
                  </div>
                </template>

                <!-- Address Column -->
                <template #[`item.address`]="{ item }">
                  <div class="text-caption">
                    {{ item.address || "No address" }}
                  </div>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                          small
                          color="primary"
                          rounded
                          :to="`/companies/${encryptIt(item.id)}`"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-eye</v-icon>
                        </v-btn>
                      </template>
                      <span>View/Edit Company</span>
                    </v-tooltip>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3">mdi-office-building-outline</v-icon>
                    <h3 class="text-h6 grey--text mt-4 mb-2">No Companies Found</h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No companies match your current filters. Try adjusting your search criteria."
                          : "You haven't added any companies yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters"
                      color="primary"
                      to="/companies/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Company
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-body-2 grey--text font-weight-medium">
                      Showing {{ (page - 1) * itemsPerPage + 1 }} to
                      {{ Math.min(page * itemsPerPage, count) }} of
                      {{ count }} companies
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      small
                      outlined
                      rounded
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-3"
                    >
                      <v-icon small left>mdi-chevron-left</v-icon>
                      Previous
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      circle
                      class="mx-2"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      small
                      outlined
                      rounded
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-3"
                    >
                      Next
                      <v-icon small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-body-2 grey--text mr-3 font-weight-medium">Go to page:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details="auto"
                      rounded
                      hide-spin-buttons
                      class="no-spin-arrows"
                      style="max-width: 90px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
export default {
  name: "companiesPage",
  data() {
    return {
      form: {
        search_keyword: "",
        is_active: "",
      },
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      jumpToPage: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      companies: [],
      statuses: [
        {
          text: "All Status",
          value: "",
        },
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      isLoading: false,
      headers: [
        { text: "Sr No.", value: "sr_no", sortable: false, width: "70px" },
        { text: "Status", value: "is_active", width: "120px" },
        {
          text: "Company Information",
          value: "company_info",
          sortable: false,
          width: "250px",
        },
        {
          text: "Contact Details",
          value: "contact",
          sortable: false,
          width: "200px",
        },
        {
          text: "Address",
          value: "address",
          sortable: false,
          width: "200px",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "120px",
          align: "center",
        },
      ],
    };
  },
  computed: {
    hasActiveFilters() {
      return this.form.search_keyword || this.form.is_active;
    },
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      console.log("Getting company data...");
      this.isLoading = true;

      // Set filters with default values
      const filters = {
        page: this.page,
        rowsPerPage: this.itemsPerPage,
        search_keyword: this.form.search_keyword || "",
        is_active: this.form.is_active || "",
      };

      let query = new URLSearchParams(filters);

      try {
        const response = await axios.get(`companies?${query}`);
        this.companies = response.data.data || [];
        this.count = response.data.count || 0;
        this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
        this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
      } catch (error) {
        console.error("Failed to fetch companies:", error);
        this.companies = [];
        this.count = 0;
      }

      this.isLoading = false;
    },

    clearAllFilters() {
      this.form = {
        search_keyword: "",
        is_active: "",
      };
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.form.search_keyword) count++;
      if (this.form.is_active) count++;
      return count;
    },

    changeItemsPerPageToggle(value) {
      if (value !== null && value !== undefined) {
        this.itemsPerPage = value;
        this.selectedItemsPerPage = value;
        this.page = 1;
        this.getData();
      }
    },

    goToPage() {
      const pageNum = parseInt(this.jumpToPage);
      if (pageNum >= 1 && pageNum <= this.paginationLength) {
        this.page = pageNum;
      } else {
        this.jumpToPage = this.page;
      }
    },

    exportToExcel() {
      // Placeholder for Excel export functionality
      console.log("Export to Excel clicked");
      // You can implement the actual Excel export logic here
    },
  },
};
</script>

<style scoped>
/* Enhanced Table Styles */
.enhanced-table >>> tbody tr {
  transition: all 0.3s ease;
}

.enhanced-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.enhanced-table >>> tbody tr:nth-child(odd) {
  background-color: #fafbfc;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px 12px 0 0;
}

.enhanced-table >>> .v-data-table-header th {
  font-weight: 600 !important;
  color: #2c3e50 !important;
  border-bottom: 2px solid #dee2e6 !important;
  padding: 16px 12px !important;
  font-size: 0.875rem !important;
}

.enhanced-table >>> tbody td {
  padding: 16px 12px !important;
  border-bottom: 1px solid #f1f3f4 !important;
}

/* Card Enhancements */
.v-card {
  border-radius: 12px !important;
  border: 1px solid #f1f3f4;
}

.v-card--outlined {
  border: 2px solid #e3f2fd !important;
}

/* Chip Enhancements */
.v-chip {
  font-weight: 500;
  border-radius: 8px !important;
}

.v-chip--pill {
  border-radius: 50px !important;
}

/* Button Enhancements */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.02em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.v-btn--rounded {
  border-radius: 8px !important;
}

/* Input Field Enhancements */
.v-text-field--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.v-text-field--outlined.v-input--is-focused >>> .v-input__control > .v-input__slot {
  border-color: #1976d2 !important;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.v-select--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

/* Avatar Enhancements */
.v-avatar {
  border: 2px solid #f1f3f4;
  transition: all 0.3s ease;
}

.v-avatar:hover {
  border-color: #1976d2;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

/* Pagination Enhancements */
.v-pagination >>> .v-pagination__item {
  border-radius: 8px !important;
  margin: 0 2px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.v-pagination >>> .v-pagination__item:hover {
  background-color: #e3f2fd !important;
  transform: translateY(-1px);
}

.v-pagination >>> .v-pagination__item--active {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.4);
}

/* Hide number input spin buttons */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
  text-align: center;
}

/* Loading Animation */
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.v-progress-circular {
  animation: pulse 2s infinite;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Spacing Utilities */
.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.gap-4 {
  gap: 16px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .enhanced-table >>> tbody td {
    padding: 12px 8px !important;
  }

  .v-card-title {
    padding: 16px !important;
  }

  .v-card-text {
    padding: 16px !important;
  }
}
</style>